import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, MessageCircle, Settings as Cog } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import PostCard from "@/components/PostCard";
import { Avatar, Empty, EarlyConfirmerBadge, FactCheckerBadge, ReportButton, Spinner, TopContributorBadge } from "@/components/common";
import { MyContributions } from "@/components/MyContributions";
import { FollowListDialog } from "@/components/FollowingList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const RelationActions = ({ profile, setProfile }) => {
  const nav = useNavigate();
  const [msgOpen, setMsgOpen] = useState(false);
  const [text, setText] = useState("Hi! ");
  const rel = profile.relation;
  const act = async (action) => {
    try {
      const { data } = await api.post(`/people/${profile.user_id}/${action}`);
      const delta = action === "follow" && !rel.following ? 1 : action === "unfollow" && rel.following ? -1 : 0;
      setProfile({ ...profile, relation: data, follower_count: profile.follower_count + delta });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async () => {
    try { const { data } = await api.post("/messages/start", { user_id: profile.user_id, text }); nav(`/messages/${data.conversation_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (rel.blocked) return <Button variant="outline" data-testid="profile-unblock-btn" onClick={() => act("unblock")}>Unblock</Button>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button data-testid="profile-follow-btn" variant={rel.following ? "outline" : "default"} className="rounded-full" onClick={() => act(rel.following ? "unfollow" : "follow")}>{rel.following ? "Following" : "Follow"}</Button>
      {rel.mutual ? <Button data-testid="profile-message-btn" variant="outline" className="rounded-full" onClick={() => setMsgOpen(true)}><MessageCircle size={15} className="mr-1.5" />Message</Button>
        : <span className="text-xs text-slate-400" data-testid="profile-message-hint">{rel.followed_by ? "Follows you · follow back to message" : "You can message each other once you follow each other"}</span>}
      <Button data-testid="profile-block-btn" variant="ghost" size="sm" onClick={() => act("block")}>Block</Button>
      <ReportButton targetType="user" targetId={profile.user_id} />
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Message {profile.name}</DialogTitle></DialogHeader>
          <Textarea data-testid="profile-message-input" value={text} onChange={(e) => setText(e.target.value)} />
          <Button data-testid="profile-message-send" onClick={send}>Send</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CountButton = ({ side, profile, onOpen, children }) => {
  const testid = side === "following" ? "profile-following-count" : "profile-follower-count";
  if (!profile.lists_visible) return <span data-testid={testid} title="This member keeps their lists private">{children}</span>;
  return <button type="button" onClick={() => onOpen(side)} data-testid={testid} className="rounded transition-colors hover:text-slate-900 hover:underline">{children}</button>;
};

export default function Profile({ self }) {
  const { me } = useMe();
  const { userId } = useParams();
  const id = self ? me.user_id : userId;
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  const [list, setList] = useState(null);
  useEffect(() => {
    setProfile(null); setError(false); setList(null);
    api.get(`/users/${id}`).then((r) => setProfile(r.data)).catch(() => setError(true));
    api.get(`/users/${id}/posts`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [id]);
  if (error) return <Empty title="Profile not available" />;
  if (!profile) return <Spinner />;
  const isMe = profile.user_id === me.user_id;

  return (
    <div className="mx-auto max-w-3xl" data-testid="profile-page">
      <div className="surface overflow-hidden">
        <div className="h-36 bg-gradient-to-br from-[#EFE4D6] to-[#E3E8E4] sm:h-48" style={profile.cover ? { background: `url(${imgUrl(profile.cover)}) center/cover` } : {}} />
        <div className="px-5 pb-6 sm:px-8">
          <div className="-mt-12 flex flex-wrap items-end justify-between gap-4">
            <Avatar user={profile} size={96} className="ring-4 ring-white" />
            {isMe ? <Link to="/settings" data-testid="profile-edit-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium hover:border-slate-400"><Cog size={15} />Edit profile & privacy</Link>
              : <RelationActions profile={profile} setProfile={setProfile} />}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2"><h1 className="text-3xl font-medium sm:text-4xl" data-testid="profile-name">{profile.name}</h1><FactCheckerBadge user={profile} testid="profile-fact-checker-badge" /><TopContributorBadge user={profile} testid="profile-top-contributor-badge" /><EarlyConfirmerBadge user={profile} testid="profile-early-confirmer-badge" /></div>
          <p className="text-sm text-slate-500">@{profile.handle}</p>
          {profile.bio && <p className="mt-3 max-w-xl text-slate-700">{profile.bio}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            {profile.suburb && <span className="inline-flex items-center gap-1"><MapPin size={14} />{profile.suburb}</span>}
            <CountButton side="following" profile={profile} onOpen={setList}><b className="text-slate-900">{profile.following_count}</b> following</CountButton>
            <CountButton side="followers" profile={profile} onOpen={setList}><b className="text-slate-900">{profile.follower_count}</b> {profile.follower_count === 1 ? "follower" : "followers"}</CountButton>
            <span><b className="text-slate-900">{profile.post_count}</b> posts</span>
          </div>
          {profile.interests?.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5">{profile.interests.map((i) => <span key={i} className="rounded-full bg-[#F5F1EB] px-3 py-1 text-xs text-slate-600">{i}</span>)}</div>}
        </div>
      </div>
      <FollowListDialog userId={profile.user_id} side={list} name={isMe ? "you" : profile.name} onClose={() => setList(null)}
        onFollowChange={(on) => isMe && setProfile((p) => ({ ...p, following_count: p.following_count + (on ? 1 : -1) }))} />
      {isMe && <MyContributions />}
      <h2 className="mb-4 mt-10 font-serif text-xl" data-testid="profile-facts-heading">Facts</h2>
      {posts === null ? <Spinner /> : posts.length === 0 ? <Empty title="No facts to show" text={isMe ? "Facts you share will appear here." : "Nothing visible to you yet."} /> :
        <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} showFollow={false} onRemoved={(pid) => setPosts(posts.filter((x) => x.id !== pid))} />)}</div>}
    </div>
  );
}
